import debug from 'debug';

// GQL
import { client } from '../client';
import { GET_ACCIDENTS_BY_IDS } from '../queries/accidents/accidents';

const log = debug('rois:gql:selectors:warmup');

// Límite del contrato backend: `accidentsByIds` acepta máx 200 ids por
// llamada. Los detalles de OT rondan las decenas de hijas, el troceo es
// defensivo.
const BATCH_SIZE = 200;

/**
 * Warm-up de la caché normalizada de Apollo para los Accidents de las hijas
 * del detalle de OT/campaña (y de los informes combinados).
 *
 * Resuelve los Accidents de todas las hijas en UNA sola query
 * `accidentsByIds` (fragment NewAccident, el MISMO de GET_ACCIDENT_BY_ID —
 * el batch backend proyecta el type Accident completo y Accident NO tiene
 * @ResolveFields: paridad sin caveats, verificado en la integración de
 * rois-back). Las entidades `Accident:<id>` quedan normalizadas en caché,
 * de modo que los `client.query(GET_ACCIDENT_BY_ID, cache-first)` per-row
 * que vienen después (cadena `selectAccidentById` del prefetch de hijas,
 * `fetchAccidentRecord` del informe combinado) se sirven de caché vía el
 * redirect `Query.accident → toReference` de policies.ts, en lugar de
 * disparar A requests fila a fila (el N+1 getAccidentById del detalle).
 *
 * REGLA DE ORO — es una OPTIMIZACIÓN, jamás un gate: si falla (tablet
 * offline en un túnel, backend viejo sin la query `accidentsByIds`, error
 * GraphQL, lo que sea) se ignora en silencio y el selector sigue
 * EXACTAMENTE como hoy — los per-row GET_ACCIDENT_BY_ID intactos son el
 * fallback natural. Este helper NUNCA lanza.
 *
 * `fetchPolicy` (default `cache-first`, contrato de tablas/informes): los
 * DETALLES (sin polling ni flag reFetch que refresque después) pasan
 * `network-only` para que CADA carga baje los Accidents frescos en un solo
 * round-trip — mismo razonamiento que `warmupOrdersByIds`.
 */
export async function warmupAccidentsByIds(
  ids: (string | undefined | null)[],
  reFetch = false,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<void> {
  // Con reFetch=true el per-row va network-only e IGNORA la caché: el
  // warm-up solo añadiría una request extra con los mismos payloads que
  // las respuestas per-row ya traen. Solo aporta en el camino cache-first.
  if (reFetch) return;
  try {
    const uniqueIds = [
      ...new Set(ids.filter((id): id is string => Boolean(id))),
    ];
    if (uniqueIds.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
    }

    await Promise.all(
      batches.map((batch) =>
        client.query({
          query: GET_ACCIDENTS_BY_IDS,
          variables: { ids: batch },
          fetchPolicy,
          // 'all' cubre "backend desplegado sin accidentsByIds" (campo
          // desconocido → error GraphQL → se ignora, no revienta).
          errorPolicy: 'all',
        })
      )
    );
  } catch (err) {
    // VACÍO de efectos a propósito: warm-up fallido = comportamiento de hoy.
    log('warm-up accidentsByIds omitido (sin efectos):', err);
  }
}
